import React, { useEffect } from 'react';
import { X, Download, ExternalLink, FileText } from 'lucide-react';

// Détermine le type d'aperçu à partir du nom de fichier.
function kindOf(nom = '') {
  const ext = nom.split('.').pop().toLowerCase();
  if (ext === 'pdf') return 'pdf';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'heic', 'svg'].includes(ext)) return 'image';
  if (['txt', 'csv'].includes(ext)) return 'text';
  return 'other';
}

/**
 * Visionneuse de document en plein écran (sans téléchargement).
 * PDF et images s'affichent directement ; les autres formats proposent le téléchargement.
 * @param {string} url        URL de visualisation (déjà avec le jeton)
 * @param {string} filename   nom affiché
 * @param {string} [downloadUrl]  URL de téléchargement (bouton « Télécharger »)
 * @param {string} [kind]   force le type d'aperçu ('pdf', 'image'…) quand le nom n'a pas d'extension
 * @param {function} onClose
 */
export default function DocViewerModal({ url, filename = 'Document', downloadUrl, kind: kindForce, onClose }) {
  const kind = kindForce || kindOf(filename);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[60] bg-navy-900/70 flex flex-col p-2 sm:p-4" onClick={onClose}>
      <div className="flex-1 flex flex-col bg-white rounded-2xl overflow-hidden shadow-2xl max-w-5xl w-full mx-auto"
        onClick={(e) => e.stopPropagation()}>
        {/* Barre d'outils */}
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-cream-200 bg-cream-50 flex-shrink-0">
          <FileText size={16} className="text-gold-600 flex-shrink-0" />
          <span className="text-[13.5px] font-medium text-navy-800 truncate flex-1" title={filename}>{filename}</span>
          <a href={url} target="_blank" rel="noopener noreferrer"
            className="p-2 rounded-lg text-navy-400 hover:bg-navy-50 hover:text-navy-700" title="Ouvrir dans un onglet">
            <ExternalLink size={16} />
          </a>
          {downloadUrl && (
            <a href={downloadUrl} target="_blank" rel="noopener noreferrer"
              className="p-2 rounded-lg text-navy-400 hover:bg-navy-50 hover:text-navy-700" title="Télécharger">
              <Download size={16} />
            </a>
          )}
          <button onClick={onClose} className="p-2 rounded-lg text-navy-400 hover:bg-red-50 hover:text-red-500" title="Fermer">
            <X size={18} />
          </button>
        </div>

        {/* Contenu */}
        <div className="flex-1 min-h-0 bg-navy-50/40 overflow-auto flex items-center justify-center">
          {kind === 'pdf' || kind === 'text' ? (
            <iframe src={url} title={filename} className="w-full h-full border-0" />
          ) : kind === 'image' ? (
            <img src={url} alt={filename} className="max-w-full max-h-full object-contain" />
          ) : (
            <div className="text-center p-8">
              <FileText size={40} className="text-navy-200 mx-auto mb-3" />
              <p className="text-[14px] text-navy-600 mb-1">Aperçu non disponible pour ce format.</p>
              <p className="text-[12.5px] text-navy-400 mb-4">(Word, Excel, présentation…) — vous pouvez le télécharger pour l'ouvrir.</p>
              {(downloadUrl || url) && (
                <a href={downloadUrl || url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-[13px] font-medium text-white bg-navy-700 hover:bg-navy-800 rounded-lg px-4 py-2">
                  <Download size={15} /> Télécharger
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
